import axios, { AxiosInstance, AxiosError } from 'axios';
import { User, LoginCredentials } from '../contexts/AuthContext';

// Configuración de la API
const API_BASE_URL = (import.meta as any).env.VITE_API_URL || 'https://crm.cainguas.cloud/api';

// Tipos de respuesta
interface LoginResponse {
  success: boolean;
  user: User;
  token: string;
  message: string;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message: string;
  error?: string;
}

// ─── Token refresh — promesa compartida para evitar race condition ───
// Si 5 requests fallan con 401 al mismo tiempo, queremos UNA sola llamada a /auth/refresh,
// no 5 paralelas (que devolverían 5 tokens distintos y se pisarían entre sí).
let _refreshPromise: Promise<string | null> | null = null;

async function refreshAuthToken(): Promise<string | null> {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      const oldToken = localStorage.getItem('dashboard_token');
      if (!oldToken) return null;
      const res = await axios.post(
        `${API_BASE_URL}/dashboard/auth/refresh`,
        {},
        { headers: { Authorization: `Bearer ${oldToken}` }, timeout: 8000 }
      );
      const newToken = res.data?.token;
      if (newToken) {
        localStorage.setItem('dashboard_token', newToken);
        return newToken;
      }
      return null;
    } catch {
      return null;
    } finally {
      // Liberar la promesa después de un tick para que requests que vinieron mientras corría
      // puedan agarrar el resultado, pero limpiamos para no cachear indefinidamente.
      setTimeout(() => { _refreshPromise = null; }, 0);
    }
  })();
  return _refreshPromise;
}

/**
 * Agrega un interceptor de response a una instancia de axios para que:
 *   1. Si recibe 401, intente renovar el JWT con /auth/refresh
 *   2. Si el refresh funciona, reintente el request original con el nuevo token
 *   3. Si el refresh falla, dispare el callback `onAuthFailure` (típicamente logout)
 * Esto se llama desde authService Y desde dashboardService/ticketService para que TODAS
 * las llamadas autenticadas hereden el auto-refresh.
 */
export function attachAuthRefreshInterceptor(
  instance: AxiosInstance,
  onAuthFailure: () => void
) {
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const original: any = error.config;
      // Si no es 401, propagar. Si ya reintentamos, propagar (evita loops).
      // Si el 401 vino del propio /auth/refresh, también propagar.
      if (
        !error.response ||
        error.response.status !== 401 ||
        original?._authRetried ||
        original?.url?.includes('/auth/refresh') ||
        original?.url?.includes('/auth/login')
      ) {
        // Errores de red sin response: mensaje más amigable
        if (!error.response) {
          return Promise.reject(new Error('Error de conexión. Verifique su conexión a internet.'));
        }
        return Promise.reject(error);
      }

      original._authRetried = true;
      const newToken = await refreshAuthToken();
      if (newToken) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return instance(original);
      }

      // Refresh falló — sesión muerta, hay que loguear de nuevo
      localStorage.removeItem('dashboard_token');
      try { onAuthFailure(); } catch { /* silencioso */ }
      return Promise.reject(error);
    }
  );
}

class AuthService {
  private api: AxiosInstance;
  private authErrorHandler: ((error: any) => void) | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Interceptor de request para agregar token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('dashboard_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Auto-refresh + retry en 401 (compartido con dashboardService/ticketService).
    attachAuthRefreshInterceptor(this.api, () => {
      if (this.authErrorHandler) {
        this.authErrorHandler({ response: { status: 401 } });
      }
    });
  }

  // Configurar manejador de errores de autenticación
  setAuthErrorHandler(handler: (error: any) => void) {
    this.authErrorHandler = handler;
  }

  removeAuthErrorHandler() {
    this.authErrorHandler = null;
  }

  // Login
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await this.api.post<LoginResponse>('/dashboard/auth/login', credentials);

      if (response.data.success && response.data.token) {
        // Configurar el token para futuras requests
        this.api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;

        return response.data;
      } else {
        throw new Error(response.data.message || 'Error en el login');
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Error al iniciar sesión. Verifique sus credenciales.');
      }
    }
  }

  // Logout
  async logout(): Promise<void> {
    const token = localStorage.getItem('dashboard_token');

    try {
      // Solo intentar cerrar sesión en el servidor si hay un token válido
      if (token && this.api.defaults.headers.common['Authorization']) {
        await this.api.post('/dashboard/auth/logout');
      }
    } catch (error) {
      // Si hay error (token expirado, etc.), solo loguearlo
      // No es crítico que falle el logout del servidor
      console.warn('Error al cerrar sesión en el servidor:', error);
    } finally {
      // Siempre limpiar token local independientemente del resultado del servidor
      localStorage.removeItem('dashboard_token');
      delete this.api.defaults.headers.common['Authorization'];
    }
  }

  // Obtener usuario actual.
  // Distingue 401 (sesión inválida → logout) de 429 (rate-limited → retry, NO logout)
  // y errores de red transitorios. AuthContext usa esos códigos para decidir si limpiar el token.
  async getCurrentUser(): Promise<User> {
    try {
      const response = await this.api.get('/dashboard/auth/me');
      // Backend returns { success: true, user: {...}, permissions: [...] }
      if (response.data.success && response.data.user) {
        return {
          ...response.data.user,
          permissions: response.data.permissions
        };
      }
      throw new Error('Respuesta inválida del servidor');
    } catch (error: any) {
      const status = error.response?.status;
      if (status === 401) {
        const err: any = new Error('Sesión expirada');
        err.authStatus = 401;
        throw err;
      }
      if (status === 429) {
        // Rate-limited — NO es problema de sesión. Que el caller reintente.
        const err: any = new Error('Demasiadas solicitudes — reintentar');
        err.authStatus = 429;
        throw err;
      }
      const err: any = new Error('Error al obtener datos del usuario');
      err.authStatus = status || 0;
      throw err;
    }
  }

  // Verificar token
  async verifyToken(): Promise<boolean> {
    try {
      const response = await this.api.get('/dashboard/auth/verify');
      return response.data.success;
    } catch (error) {
      return false;
    }
  }

  // Cambiar contraseña
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      const response = await this.api.post<ApiResponse>('/dashboard/auth/change-password', {
        currentPassword,
        newPassword,
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al cambiar contraseña');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al cambiar contraseña');
    }
  }

  // Actualizar perfil
  async updateProfile(profileData: Partial<User>): Promise<User> {
    try {
      const response = await this.api.put<ApiResponse<User>>('/dashboard/auth/profile', profileData);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Error al actualizar perfil');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al actualizar perfil');
    }
  }

  // Solicitar restablecimiento de contraseña
  async requestPasswordReset(email: string): Promise<void> {
    try {
      const response = await this.api.post<ApiResponse>('/dashboard/auth/forgot-password', {
        email,
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al solicitar restablecimiento');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al solicitar restablecimiento');
    }
  }

  // Restablecer contraseña
  async resetPassword(token: string, newPassword: string): Promise<void> {
    try {
      const response = await this.api.post<ApiResponse>('/dashboard/auth/reset-password', {
        token,
        newPassword,
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al restablecer contraseña');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al restablecer contraseña');
    }
  }

  // Obtener configuración de usuario
  async getUserSettings(): Promise<any> {
    try {
      const response = await this.api.get<ApiResponse>('/dashboard/auth/settings');

      if (response.data.success) {
        return response.data.data || {};
      } else {
        throw new Error(response.data.message || 'Error al obtener configuración');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al obtener configuración');
    }
  }

  // Actualizar configuración de usuario
  async updateUserSettings(settings: any): Promise<void> {
    try {
      const response = await this.api.put<ApiResponse>('/dashboard/auth/settings', settings);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al actualizar configuración');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Error al actualizar configuración');
    }
  }

  // Obtener logs de actividad del usuario
  async getActivityLogs(limit: number = 50): Promise<any[]> {
    try {
      const response = await this.api.get<ApiResponse<any[]>>(`/dashboard/auth/activity?limit=${limit}`);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        return [];
      }
    } catch (error: any) {
      console.error('Error al obtener logs de actividad:', error);
      return [];
    }
  }

  // Validar permisos específicos
  async validatePermission(permission: string): Promise<boolean> {
    try {
      const response = await this.api.post<ApiResponse<boolean>>('/dashboard/auth/validate-permission', {
        permission,
      });

      return response.data.success && response.data.data === true;
    } catch (error) {
      return false;
    }
  }

  // Obtener información de sesión
  async getSessionInfo(): Promise<any> {
    try {
      const response = await this.api.get<ApiResponse>('/dashboard/auth/session');

      if (response.data.success) {
        return response.data.data || {};
      } else {
        return {};
      }
    } catch (error) {
      return {};
    }
  }
}

// Crear instancia singleton
export const authService = new AuthService();

export default authService;